import { ElementRef, inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { accounts, GsiButtonConfiguration } from 'google-one-tap';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { GoogleTokenResponseModel } from 'app/shared/models/google-token-response.model';

import { ExternalScriptService } from './external-script.service';

import { environment } from 'env/environment';

// `google` is injected at runtime by the GSI script loaded through ExternalScriptService.
declare const google: { accounts: accounts };

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class GoogleSignOnService {
  private readonly scriptService = inject(ExternalScriptService);

  private token$ = new Subject<GoogleTokenResponseModel>();
  private initializer$ = new ReplaySubject<boolean>();
  private sourceId = '';

  public constructor() {
    this.initializeGoogleAccounts();
  }

  private initializeGoogleAccounts(): void {
    this.scriptService.load('google').then((data) => {
      google.accounts.id.initialize({
        client_id: environment.googleClientId,
        callback: (response) => this.token$.next({
          token: response.credential,
          sourceId: this.sourceId
        })
      });
      this.initializer$.next(true);
    }).catch((error: unknown) => console.error(error));
  }

  public render(button: ElementRef, options: GsiButtonConfiguration, sourceId: string) {
    this.initializer$.asObservable().pipe(
      take(1),
      untilDestroyed(this)
    )
      .subscribe((_) => this.renderButton(button, options, sourceId));
  }

  public getIdToken(): Observable<GoogleTokenResponseModel> {
    return this.token$.asObservable();
  }

  private renderButton(button: ElementRef, options: GsiButtonConfiguration, sourceId: string) {
    google.accounts.id.renderButton(
      button.nativeElement,
      {
        ...options,
        click_listener: () => this.sourceId = sourceId
      }
    );
  }
}
